import { useCallback, useEffect, useRef, useState } from 'react';
import { Model, ModelPacks } from 'scripts/model-loader';
import VoxelPlanet from 'scripts/objects/voxel-planet';
import templates from 'scripts/objects/voxel-planet/templates';
import VoxelEditor, { EditorTool, EditorTools } from 'scripts/scenes/voxel-editor';
import { naturalSort } from 'scripts/utility';
import Engine from 'scripts/engine/engine';
import TemplatePlanetModal, { TemplatePlanet } from 'components/modals/TemplatePlanetModal';

interface ToolbarProps {
    editor: VoxelEditor
} 

const defaults = {
    color: 0,
    brushSize: '50',
    tool: undefined,
}

const templatePlanets: TemplatePlanet[] = [
    {
        title: "Default",
        icon: 'earth-ico',
        data: templates.default
    },
    {
        title: "Empty",
        icon: 'empty-ico',
        data: templates.empty
    },
]

function VoxelToolbar(props: ToolbarProps) {
    const { editor } = props;
    const [hideToolSettings, setHideToolSettings] = useState<boolean>(false);
    const [undoRedoCount, setUndoRedoCount] = useState<number[]>([0,0]);
    const [selectedTool, setSelectedTool] = useState<EditorTool | undefined>(defaults.tool);
    const [selectedColor, setSelectedColor] = useState<number>(0);
    const [selectedObject, setSelectedObject] = useState<Model | undefined>(undefined);
    const [colorPalette, setColorPalette] = useState<string[]>([]);
    const [templateModalOpen, setTemplateModalOpen] = useState<boolean>(false);
    const inputFile = useRef<any>();

    const resetTools = useCallback(() => {
        setColorPalette(editor.planet.colorPalette.colors.map(c => `#${c.getHexString()}`));
    }, [editor])

    useEffect(() => {
        resetTools();
        editor.grid.visible = true;
        editor.selectionBox.visible = true;
        editor.history.onUndoRedoChange.addListener('TOOLBAR', (undos: number, redos: number) => setUndoRedoCount([undos, redos]));
        editor.planet.colorPalette.onAfterChange.addListener('TOOLBAR', (colors: any[]) => {
            setColorPalette(colors.map(c => `#${c.getHexString()}`));
        });
        editor.onUpdateCallback.addListener('TOOLBAR', () => {
            resetTools();
        });

        return () => {
            editor.onUpdateCallback.removeListener('TOOLBAR');
        }
    }, [editor])

    const onSetSelectedTool = (tool: EditorTool) => {
        if (selectedTool === tool) {
            setSelectedTool(undefined);
            editor.tool = undefined;
        } else {
            setHideToolSettings(false);
            setSelectedTool(tool);
            editor.tool = tool;
        }
    }

    const onSetSelectedColor = (color: number) => {
        setSelectedColor(color);
        editor.color = color;
    }

    const onSetPaletteColor = (index: number, color: string) => {
        const newColors = [...colorPalette];
        newColors[index] = color;
        setColorPalette(newColors);
        editor.planet.colorPalette.setColors(newColors);
    }

    const onToggleGrid = () => {
        editor.grid.visible = !editor.grid.visible;
    }

    const getToolName = () => {
        if (selectedTool) {
            return selectedTool.name;
        }
        return '';
    }

        const onSetSelectedObject = (model: Model) => {
        setSelectedObject(model);
        editor.model = model;
    }

    const onExportPlanet = () => {
        editor.planet.export(`Planet`);
    }

    const onTakeScreenshot = () => {
        let wasGridVisible = false;
        if (editor.grid.visible) {
            onToggleGrid();
            wasGridVisible = true;
        }
        Engine.instance.takeScreenshot(`voxel_planet.png`);
        if (wasGridVisible) {
            onToggleGrid();
        }
    }

    const onImportPlanet = () => {
        if (inputFile && inputFile.current) {
            inputFile.current.click();
        }
    }

    const onUploadFile = (e: any) => {
        const files = e.target.files;
        if (files.length > 0) {
            const f = files[0];
            const reader = new FileReader();
            reader.onload = (data => {
                try {
                    if (!editor.loadData(data.target?.result as string)) {
                        alert(`Invalid ${VoxelPlanet.FORMAT} format!`);
                    }
                    resetTools();
                } catch (error) {
                    console.log(error)
                    alert('Unexpected upload value.  Please check the file format.')
                }
            });
            reader.readAsText(f);
            e.target.value = null;
        }
    }

    const loadTemplate = (data: string) => {
        editor.loadData(data)
        resetTools();
    }

    const renderTopTools = () => {
        return (
            <div id="tool-list">
                <div className={`tool-item ${undoRedoCount[0] > 0 ? '' : 'disabled'}`} onClick={() => editor.undo()}>
                    <svg>
                        <use href='#undo' />
                    </svg>
                    <span className="tooltip-below">Undo</span>
                </div>
                <div className="tool-item" onClick={() => setTemplateModalOpen(true)}>
                    <svg>
                        <use href='#earth-ico' />
                    </svg>
                    <span className="tooltip-below">Templates</span>
                </div>
                <div className={`tool-item ${undoRedoCount[1] > 0 ? '' : 'disabled'}`} onClick={() => editor.redo()}>
                    <svg>
                        <use href='#redo' />
                    </svg>
                    <span className="tooltip-below">Redo</span>
                </div>
            </div>
        )
    }

    const renderTools = () => {
        return (
            <div id="tool-list">
                {Object.values(EditorTools).map(tool =>
                    <div key={tool.name} className={`tool-item ${selectedTool === tool ? 'selected' : ''}`} onClick={() => onSetSelectedTool(tool)}>
                        <svg>
                            <use href={`#${tool.icon}`} />
                        </svg>
                        <span className="tooltip">{tool.description}</span>
                    </div>
                )}
            </div>
        )
    }

    const renderToolTitle = () => {
        if (!selectedTool) {
            return '';
        }
        return (
            <div id="title" onClick={() => setHideToolSettings(!hideToolSettings)}>
                <svg width="24" height="24">
                    <use href={hideToolSettings ? "#up-chevron" : "#down-chevron"}/>
                </svg>
                {getToolName()}
            </div>
        )
    }

    const renderToolInfo = () => {
        if (hideToolSettings) {
            return '';
        }
        if (selectedTool === EditorTools.items) {
            return (
                <div id="scenery-items">
                    {ModelPacks[0].models.sort((a, b) => naturalSort(a.sort, b.sort)).map(model =>
                        <div key={model.id} className={`scenery-item ${selectedObject === model ? 'selected' : ''}`} onClick={() => onSetSelectedObject(model)}>{model.name}</div>
                    )}
                </div>
            );
        }
        if (selectedTool === EditorTools.settings) {
            return <div id="tool-settings">
                <div className="tool-item" onClick={() => onToggleGrid()}>
                    <svg>
                        <use href={'#grid'} />
                    </svg>
                    <span className="tooltip">Toggle Grid</span>
                </div>
                <div key={'download'} className="tool-item" onClick={() => onExportPlanet()}>
                    <svg>
                        <use href={'#download'} />
                    </svg>
                    <span className="tooltip">Download planet</span>
                </div>
                <div key={'upload'} className="tool-item" onClick={() => onImportPlanet()}>
                    <svg>
                        <use href={'#upload'} />
                    </svg>
                    <span className="tooltip">Upload planet</span>
                    <input ref={inputFile} type='file'
                        accept=".txt"
                        style={{ display: 'none' }}
                        onChange={onUploadFile}
                    />
                </div>
                <div key={'screenshot'} className="tool-item" onClick={() => onTakeScreenshot()}>
                    <svg>
                        <use href={'#camera'} />
                    </svg>
                    <span className="tooltip">Take screenshot</span>
                </div>
            </div>
        }
        return '';
    }

    const renderColors = () => {
        if (!selectedTool || hideToolSettings || ![EditorTools.paint, EditorTools.add, EditorTools.items].includes(selectedTool)) {
            return '';
        }
        return (
            <div id="tool-colors">
                {colorPalette.map((c, index) =>
                    <div key={index}>
                        <div className={`color-item ${index === selectedColor ? 'selected' : ''}`}
                            style={{ backgroundColor: c }} onClick={() => onSetSelectedColor(index)} />
                    </div>
                )}
                <div className="color-picker">
                    <svg>
                        <use href="#pencil" />
                    </svg>
                    <input onChange={e => onSetPaletteColor(selectedColor, e.target.value)} type="color" value={colorPalette[selectedColor]} />
                </div>
            </div>
        );
    }

    return (
        <>
            <div id="toolbar-component">
                <div id="top-toolbar">
                    <div id="toolbar">
                        {renderTopTools()}
                    </div>
                </div>
                <div id="bottom-toolbar">
                    <div id="toolbar">
                        {renderToolTitle()}
                        {renderColors()}
                        {renderToolInfo()}
                        {renderTools()}
                    </div>
                </div>
            </div>
            {templateModalOpen && <TemplatePlanetModal templates={templatePlanets} onClose={() => setTemplateModalOpen(false)} onSelect={loadTemplate}></TemplatePlanetModal>}
        </>
    );
}

export default VoxelToolbar;
